import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {boolean} */
  #opening = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #openTimer = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
    if (this.#openTimer !== null) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }
    this.#opening = false;
    this.removeAttribute('data-dialog-opening');
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * Resolve the live dialog element. Cart morphs can replace the node and
   * leave `refs.dialog` pointing at a detached element.
   * @returns {HTMLDialogElement | null}
   */
  #getDialog() {
    const fromRef = this.refs.dialog;
    if (fromRef instanceof HTMLDialogElement && fromRef.isConnected) {
      return fromRef;
    }

    const fresh = this.querySelector('dialog');
    if (fresh instanceof HTMLDialogElement) {
      // Keep refs in sync after morph/section updates.
      this.refs.dialog = fresh;
      return fresh;
    }

    return null;
  }

  /**
   * Cheap visual priming for pointerdown — paints before the click task runs (INP).
   */
  primeOpen = () => {
    const dialog = this.#getDialog();
    if (!dialog || dialog.open || this.#opening) return;
    this.setAttribute('data-dialog-opening', '');
  };

  /**
   * Clears a stale prime overlay when the gesture is cancelled / not followed by open.
   */
  clearPrime = () => {
    if (!this.#opening) {
      const dialog = this.#getDialog();
      if (!dialog?.open) {
        this.removeAttribute('data-dialog-opening');
      }
    }
  };

  /**
   * Shows the dialog.
   *
   * Sync work is only a paint hint. Heavy showModal / scroll-lock runs in a
   * new macrotask so the interaction can paint under 100ms INP.
   */
  showDialog = () => {
    const dialog = this.#getDialog();
    if (!dialog) return;

    // If a previous open attempt got stuck, recover instead of no-oping forever.
    if (this.#opening && this.#openTimer === null && !dialog.open) {
      this.#opening = false;
    }

    if (dialog.open || this.#opening) return;

    this.#opening = true;
    this.#previousScrollY = window.scrollY;
    this.setAttribute('data-dialog-opening', '');

    if (this.#openTimer !== null) clearTimeout(this.#openTimer);

    // Macrotask boundary: lets the browser paint and end the interaction.
    this.#openTimer = setTimeout(() => {
      this.#openTimer = null;
      this.#mountDialog();
    }, 0);
  };

  #mountDialog() {
    const dialog = this.#getDialog();

    if (!dialog) {
      this.#opening = false;
      this.removeAttribute('data-dialog-opening');
      return;
    }

    this.removeAttribute('data-dialog-opening');

    if (dialog.open) {
      this.#opening = false;
      return;
    }

    const scrollY = this.#previousScrollY;

    try {
      // First dialog paint skips heavy descendants (see .dialog-opening CSS).
      dialog.classList.add('dialog-opening');
      dialog.showModal();
    } catch (error) {
      console.error(error);
      dialog.classList.remove('dialog-opening');
      this.#opening = false;
      return;
    }

    // Scroll-lock + listeners after open paint; avoids open-click closing the dialog.
    requestAnimationFrame(() => {
      const liveDialog = this.#getDialog();

      if (!liveDialog?.open) {
        liveDialog?.classList.remove('dialog-opening');
        this.#opening = false;
        return;
      }

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      liveDialog.classList.remove('dialog-opening');

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);

      this.dispatchEvent(new DialogOpenEvent());
      this.#opening = false;
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    if (this.#openTimer !== null) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }

    this.removeAttribute('data-dialog-opening');
    this.#opening = false;

    const dialog = this.#getDialog();
    if (!dialog?.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    dialog.classList.remove('dialog-opening');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    const dialog = this.#getDialog();
    if (!dialog) return;

    if (dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const dialog = this.#getDialog();
    if (!dialog) return;

    // Ignore clicks on host chrome outside the dialog (e.g. cart icon button).
    // Those triggers own open/close via on:click — treating them as "outside"
    // caused open-then-immediate-close races after add-to-cart.
    if (
      event.target instanceof Node &&
      this.contains(event.target) &&
      !dialog.contains(event.target) &&
      !(event.target instanceof HTMLDialogElement)
    ) {
      return;
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

// Prime dialog overlays on pointerdown for marked triggers (search, etc.).
document.addEventListener(
  'pointerdown',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-dialog-prime]');
    if (!trigger) return;

    const id = trigger.getAttribute('data-dialog-prime');
    const host = id ? document.getElementById(id) : null;
    if (host instanceof DialogComponent) {
      host.primeOpen();
    }
  },
  { passive: true }
);

document.addEventListener(
  'pointerup',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-dialog-prime]');
    if (!trigger) return;

    const id = trigger.getAttribute('data-dialog-prime');
    const host = id ? document.getElementById(id) : null;
    if (host instanceof DialogComponent) {
      setTimeout(() => host.clearPrime(), 0);
    }
  },
  { passive: true }
);
